import { useMemo } from 'react';
import styled from 'styled-components';
import { Form, Spin } from 'antd';
import aptSelectors from 'services/selectors/apt';
import aptActions from 'services/actions/apt';
import { useAppDispatch } from 'store/hooks';
import useCallerId from 'widgets/CallerId/useCallerId';
import AddCustomer from 'widgets/AddCustomer';
import { modalConfirmRef } from 'widgets/ModalConfirm';
import CallerCard from './CallerCard';
import { ICallerCallItem } from '../types';
import { mapPendingToCallItem } from '../mappers';

// Scrollable list of incoming-call cards — all cards always expanded (caller info
// on the left, SMS/BOOK/SKIP on the right). The pending list is newest→oldest, so the
// latest call lands on top and keeps pulsing (like the floating caller button)
// until a newer call replaces it.
const CallerListTab = () => {
  const navigatorForm = Form.useFormInstance();
  const dispatch = useAppDispatch();
  const callerSvc = useCallerId();
  const pending = aptSelectors.pendingCallers.data();
  const loading = aptSelectors.pendingCallers.loading();
  const items = useMemo(() => pending.map(mapPendingToCallItem), [pending]);
  const topId = items[0]?.id ?? null; // newest caller → the one that pulses

  const onBook = (item: ICallerCallItem) => async () => {
    let target = item;
    // Customer not in the system → require creating one (name mandatory) before
    // booking. Cancelling the modal aborts (the promise only resolves on save).
    if (!item.customerId) {
      const created = await AddCustomer.add({ phoneNumber: item.phone, requireFirstName: true });
      if (!created?.id) return;
      target = { ...item, customerId: created.id, name: created.name };
    }
    navigatorForm.setFieldValue('activeTab', 'caller-detail');
    navigatorForm.setFieldValue('activeItem', { ...target });
    dispatch(aptActions.getCustomerRecent.fetch(Number(target.customerId)));
  };

  const onSms = (item: ICallerCallItem) => async () => {
    await callerSvc.sms(item.id, { id: item.customerId ?? null, name: item.name, phone: item.phone });
    // SMS handled this caller → refresh the pending list, close the panel if empty.
    dispatch(aptActions.getPendingCallers.fetch({ closeIfEmpty: true }));
  };

  // Skip không có undo nên hỏi lại trước, tránh bấm nhầm mất caller thật khỏi list.
  const onSkip = (item: ICallerCallItem) => () => {
    modalConfirmRef.current?.open(async () => {
      const ok = await callerSvc.skip(item.id);
      if (!ok) return; // lỗi đã báo bằng toast; giữ nguyên card để bấm lại
      // Caller spam đã được đánh dấu → làm mới list, đóng panel nếu rỗng.
      dispatch(aptActions.getPendingCallers.fetch({ closeIfEmpty: true }));
    }, 'Skip call', 'Are you trying to skip the call?', { okText: 'Yes', cancelText: 'Cancel' });
  };

  if (loading && !items.length) {
    return <Centered><Spin /></Centered>;
  }
  if (!items.length) {
    return <Centered><Empty>No incoming calls</Empty></Centered>;
  }

  return (
    <List>
      {items.map((item) => (
        <CallerCard
          key={item.id}
          item={item}
          pulse={topId === item.id}
          onSms={onSms(item)}
          onBook={onBook(item)}
          onSkip={onSkip(item)}
        />
      ))}
    </List>
  );
};

export default CallerListTab;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  padding: 16px;
  overflow-y: auto;
  box-sizing: border-box;
  background: transparent;
`;

const Centered = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 24px;
`;

const Empty = styled.span`
  color: #86909c;
  font-family: Poppins;
  font-size: 16px;
`;
